import React, { useState, useRef, useEffect } from "react";
import { Polygon, Polyline, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import ConstantsImages from "../../../constants/ConstantsImages";
import ConstantsTexts from "../../../constants/ConstantsTexts";
import { DivColumn, DivRow, ParagraphText } from "../../../styles/SharedStyles";
import WidgetIconFive from "../../icons/widget-icon-five/WidgetIconFive";
import UtilsUnit from "../../../utils/UtilsUnit";
import ConstantsMap from "../../../constants/ConstantsMap";
import { wmResolution, wmZoomForResolution } from "../../../constants/MapCRS";
import WidgetMapLayout from "../widget-map-layout/WidgetMapLayout";
import { useWidgetTerminalInfoMap } from "./useWidgetTerminalInfoMap";
import "./WidgetTerminalInfoMap.css";

function SelectedFeaturePopup({ selectedMarker, contentRef }) {
  const map = useMap();
  useEffect(() => {
    if (!selectedMarker || !map) return;
    // Defer so ref is populated after React commit
    const id = requestAnimationFrame(() => {
      if (!contentRef.current) return;
      const content = contentRef.current.cloneNode(true);
      content.style.display = "";
      L.popup().setLatLng([selectedMarker.latitude, selectedMarker.longitude]).setContent(content).openOn(map);
    });
    return () => {
      cancelAnimationFrame(id);
      map.closePopup();
    };
  }, [selectedMarker, map, contentRef]);
  return null;
}

function WidgetTerminalInfoMap({ markers, heightProp, widthProp, nationalModelForTerminalData, selectedBusLinesData = [], uploadedPolygon }) {
  const [mapType, setMapType] = useState("carto");
  const [mapState, setMapState] = useState(null);
  const popupContentRef = useRef(null);
  const terminalId = nationalModelForTerminalData?.terminalID;
  const prevTerminalIdRef = React.useRef(undefined);

  // When switching terminals in PageFilter, clear map state so fitMapBounds runs again and the new terminal is shown
  React.useEffect(() => {
    if (prevTerminalIdRef.current !== undefined && prevTerminalIdRef.current !== terminalId) {
      setMapState(null);
    }
    prevTerminalIdRef.current = terminalId;
  }, [terminalId]);

  const {
    offsetSharedCoordinates,
    offsetAmount,
    handlePolylineClick,
    polygonGeoJson,
    getPolygonCenter,
    handlePolygonClick,
    setMapRef,
    fitMapBounds,
    handleCloseMarkerClick,
    handleMarkerClick,
    selectedMarker,
    mapContainerRef,
  } = useWidgetTerminalInfoMap({
    nationalModelForTerminalData,
    selectedBusLinesData,
    markers,
    uploadedPolygon,
  });

  const defaultCenter = UtilsUnit.getCenterPropForMap();
  const lat = mapState?.center?.lat ?? defaultCenter.lat;
  const lng = mapState?.center?.lng ?? defaultCenter.lng;
  const center = Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : [defaultCenter.lat, defaultCenter.lng];
  const resolution = mapState?.resolution ?? wmResolution(ConstantsMap.DEFAULT_ZOOM, center[0]);
  const zoom = wmZoomForResolution(resolution, center[0]);

  const busIcon = L.icon({
    iconUrl: ConstantsImages.BUS_PARKING_IMAGE,
    iconSize: [UtilsUnit.remToPx(3), UtilsUnit.remToPx(3)],
    iconAnchor: [UtilsUnit.remToPx(1.5), UtilsUnit.remToPx(1.5)],
  });

  const busIconInside = L.icon({
    iconUrl: ConstantsImages.BUS_PARKING_TWO_IMAGE,
    iconSize: [UtilsUnit.remToPx(3), UtilsUnit.remToPx(3)],
    iconAnchor: [UtilsUnit.remToPx(1.5), UtilsUnit.remToPx(1.5)],
  });

  function polyline_ui(index, busLine) {
    const isLargeSelection = selectedBusLinesData.length >= 30;
    const colorLine = "#0431FF33";
    const coords = isLargeSelection
      ? busLine.geojson.coordinates.map(([lng, lat]) => [lat, lng])
      : offsetSharedCoordinates(selectedBusLinesData, index, offsetAmount).map(([lng, lat]) => [lat, lng]);
    const strokeColor = isLargeSelection ? colorLine : busLine.color || colorLine;
    return (
      <Polyline
        key={index}
        positions={coords}
        pathOptions={{ color: strokeColor, weight: 3 }}
        eventHandlers={{
          click: (e) => {
            if (!isLargeSelection) {
              if (e.originalEvent) e.originalEvent.stopPropagation();
              handlePolylineClick({
                latitude: e.latlng.lat,
                longitude: e.latlng.lng,
                terminalName: busLine.RouteShortName,
                erezSKU: busLine.FormattedRouteName,
              });
            }
          },
        }}
      />
    );
  }

  function polygon_ui() {
    return (
      polygonGeoJson?.type === "MultiPolygon" &&
      polygonGeoJson.coordinates.map((polygon, index) => {
        const centerPt = getPolygonCenter(polygon);
        const positions = polygon[0].map(([lng, lat]) => [lat, lng]);
        return (
          <Polygon
            key={index}
            positions={positions}
            pathOptions={ConstantsMap.getTerminalPolygonPathOptions(mapType)}
            eventHandlers={{
              click: (e) => {
                if (e.originalEvent) e.originalEvent.stopPropagation();
                handlePolygonClick({
                  latitude: centerPt.lat,
                  longitude: centerPt.lng,
                  terminalID: nationalModelForTerminalData.terminalID,
                  terminalName: nationalModelForTerminalData.terminalName,
                  erezSKU: nationalModelForTerminalData.erezSKU,
                  municipalityName: nationalModelForTerminalData.municipalityName,
                  terminalOwnership: nationalModelForTerminalData.terminalOwnership,
                  type: "polygon",
                });
              },
            }}
          />
        );
      })
    );
  }

  function marker_ui(marker) {
    return (
      <Marker
        key={marker.stop_code}
        position={[marker.stop_lat, marker.stop_lon]}
        icon={marker.isInsideTerminal ? busIconInside : busIcon}
        eventHandlers={{
          click: (e) => {
            if (e.originalEvent) e.originalEvent.stopPropagation();
            handleMarkerClick({
              latitude: marker.stop_lat,
              longitude: marker.stop_lon,
              terminalName: marker.stop_name,
              erezSKU: marker.stop_code,
            });
          },
        }}
      />
    );
  }

  function info_window_ui() {
    if (selectedMarker?.type === "polyline") {
      return info_window_polyline_ui();
    } else if (selectedMarker?.type === "polygon") {
      return info_window_polygon_ui();
    } else {
      return info_window_marker_ui();
    }
  }

  function info_window_polyline_ui() {
    return (
      <DivColumn className="info-window-frame-1-marker">
        <DivColumn className="info-window-frame-3-marker">
          <ParagraphText className="info-window-title-1-polyline">{selectedMarker.terminalName}</ParagraphText>
          <ParagraphText className="info-window-title-2-polyline">{selectedMarker.erezSKU}</ParagraphText>
        </DivColumn>
      </DivColumn>
    );
  }

  function info_window_polygon_ui() {
    return (
      <DivColumn className="info-window-frame-1-polygon">
        <DivColumn className="info-window-frame-4-polygon">
          <ParagraphText className="info-window-title-1-polygon">{selectedMarker.terminalName}</ParagraphText>
          <ParagraphText className="info-window-title-2-polygon">{selectedMarker.erezSKU}</ParagraphText>
        </DivColumn>
        <DivColumn className="info-window-frame-2-polygon">
          <DivRow className="info-window-frame-3-polygon">
            <div style={{ height: "1.15rem", width: "1rem" }}>
              <WidgetIconFive iconProp={ConstantsImages.MARKER_IMAGE} widthIconProp="1.4rem" heightIconProp="1.4rem" /> {/* 1.2rem x 1.4rem */}
            </div>
            <ParagraphText className="info-window-title-3-polygon">{selectedMarker.municipalityName}</ParagraphText>
          </DivRow>
          <ParagraphText className="info-window-title-3-polygon">
            {ConstantsTexts.CODE_125}: {selectedMarker.terminalOwnership}
          </ParagraphText>
        </DivColumn>
      </DivColumn>
    );
  }

  function info_window_marker_ui() {
    if (!selectedMarker) return null;
    return (
      <DivColumn className="info-window-frame-1-marker">
        <DivColumn className="info-window-frame-3-marker">
          <ParagraphText className="info-window-title-1-marker">{selectedMarker.terminalName}</ParagraphText>
          <ParagraphText className="info-window-title-2-marker">{selectedMarker.erezSKU}</ParagraphText>
        </DivColumn>
      </DivColumn>
    );
  }

  return (
    <WidgetMapLayout
      containerRef={mapContainerRef}
      className="widget-terminal-info-map"
      style={{ height: heightProp, width: widthProp, position: "relative" }}
      onClick={handleCloseMarkerClick}
      center={center}
      zoom={zoom}
      setMapRef={setMapRef}
      fitMapBounds={fitMapBounds}
      skipFitBounds={!!mapState}
      onMapStateChange={setMapState}
      mapType={mapType}
      setMapType={setMapType}
    >
      {selectedBusLinesData.map((busLine, index) => polyline_ui(index, busLine))}
      {polygon_ui()}
      {markers && markers.map(marker_ui)}
      {selectedMarker && (
        <>
          <SelectedFeaturePopup selectedMarker={selectedMarker} contentRef={popupContentRef} />
          <div ref={popupContentRef} className="widget-terminal-info-popup-content" style={{ display: "none" }}>
            {info_window_ui()}
          </div>
        </>
      )}
    </WidgetMapLayout>
  );
}

export default React.memo(WidgetTerminalInfoMap);
